import _ from 'lodash';
import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import debug from 'debug';
import produce from 'immer';
import { isLayerType } from 'common/utils/is-layer-type';
import { orderMediaQueryConfig } from 'common/utils/story-media-query';
import {
	COMPONENT_STATES_ORDERED,
	COMPONENT_STATES,
	CUSTOM_CSS,
	CARD_ROOT_ELEMENT_INDEX,
	COMPONENT_TYPE,
	GLOBAL_BLOCK_DISPLAY,
	CSS_PROPS,
	DEFAULT_MEDIA_QUERY_PLATFORMS,
	COMPONENT_STATES_LIST,
} from 'common/constants';
import { attrSelector } from 'client/utils';
import { getElementSymbol } from 'client/components/common/BuildingBlocks/utils/traverse-tree';
import { BBModel, StoryMediaQuery, StorySymbols } from 'types/story';
import { getCustomCssProp } from 'utils/get-custom-css-prop';

type Props = {
	elements: BBModel[];
	mediaQuery: StoryMediaQuery;
	isCardTree: boolean;
	isEditor: boolean;
	symbols?: StorySymbols;
};

type MapCssObject = {
	cssObject: Record<string, string>;
	selector: string;
	addTab?: boolean;
	element: BBModel;
	mq: ReturnType<typeof orderMediaQueryConfig>;
};

/*------------------------------------------------------------------------
Styles

Order:
1 first, the usual selectors (id) ordered by media queries
2 then state overrides (state data attribute selector)

Selector types:
1. id
2. id + state data attribute selector

How state selector works
- When working in client side, we manage attributes in Component to apply specific state styles to DOM element
- When working in editor:
    -> initialize state in Admin (cardEditorReducer.state)
     -> transmit state to client
      -> assign state selector to "uiConfig.nodeProps.<data-state>"
        -> assigning this attr in DOM
------------------------------------------------------------------------*/

class StylesRenderer extends Component<Props> {
	log = debug('client:components:Styles');

	/**
	 * @info 💡💡💡 If you're adding a new compound selector,
	 * you've to add his implementation in "traverseTree" and his getComputedValue too
	 *
	 * @description get css selector accordingly to state and environment
	 * @param state {String}
	 * @param isEditor {Boolean}
	 * @return {*}
	 */
	stateSelector = ({ state }) => {
		return state === COMPONENT_STATES.DEFAULT ? '' : attrSelector(state);
	};

	/**
	 * mapChildren -> mapStyles (mapStates, mapMediaQueries) -> mapCssObject
	 * @return {string}
	 */
	transformStylesToString() {
		const { elements, mediaQuery } = this.props;
		let styles = '';
		const t0 = performance.now();

		// eg. default, min1024, min1600, max800 max700
		const orderedMediaQueryArray = orderMediaQueryConfig(mediaQuery, {
			defaultFirst: true,
			minOrder: 'asc',
			maxOrder: 'desc',
		});

		const mapChildren = (children, parent = {}) => _.forEach(children, child => mapStyles(child, parent));

		/**
		 * Inject style stored in other component (source) to the desired one (destination)
		 * @param state
		 * @param mq
		 * @param destination
		 * @param element
		 */
		const injectStyles = ({ state, mq, destination, element }) => {
			const cardOtherProps = elements[CARD_ROOT_ELEMENT_INDEX[COMPONENT_TYPE.CARD]].uiConfig.componentProps.other;

			const source = {
				[COMPONENT_TYPE.CONTENT]: {
					height: _.get(cardOtherProps, `${state}.${mq}.contentHeight`),
					marginTop: _.get(cardOtherProps, `${state}.${mq}.contentMarginTop`),
					marginBottom: _.get(cardOtherProps, `${state}.${mq}.contentMarginBottom`),
					marginLeft: _.get(cardOtherProps, `${state}.${mq}.contentMarginLeft`),
					marginRight: _.get(cardOtherProps, `${state}.${mq}.contentMarginRight`),
				},
			};

			return produce(destination, draft => {
				if (source[element.type]) {
					Object.assign(draft, _.omitBy(source[element.type], _.isUndefined));
				}
			});
		};

		const mapStyles = (elementOrigin, parent) => {
			const element = getElementSymbol(elementOrigin, this.props.symbols || {});
			const elementStyles = _.get(element, 'uiConfig.componentProps.styles');
			const { isCardTree } = this.props;
			// skip story elements in card elements render
			if (isLayerType(element).global && isCardTree) {
				return;
			}

			if (!elementStyles) {
				// go recursively down children
				mapChildren(element.children);
				return;
			}

			const id = _.get(element, 'uiConfig.nodeProps.id');

			const nextParent = { ...parent };

			const owsStates = COMPONENT_STATES_ORDERED.filter(s => _.includes(COMPONENT_STATES_LIST[element.type], s));
			const states = _.size(parent.states) ? parent.states : owsStates;
			const uniqStates = _.uniq([COMPONENT_STATES.DEFAULT, ...states]);

			_.forEach(uniqStates, state => {
				_.forEach(orderedMediaQueryArray, mq => {
					const { config, key: platform } = mq;
					const mediaProperty = config.minWidth ? 'min-width' : 'max-width';
					const mediaValue = Math.max(config.minWidth, config.maxWidth);
					const media = `@media (${mediaProperty}: ${mediaValue}px)`;

					// pick element styles for current media query
					let cssObject = _.get(elementStyles, [state, platform]) || {};
					// @ts-expect-error ts-migrate FIXME
					const stateSelector = this.stateSelector({ state, isEditor: this.props.isEditor });

					let selector = `#${id}${stateSelector}`;
					if (parent.id && parent.id !== id && stateSelector) {
						selector = `#${parent.id}${stateSelector} #${id}`;
					}

					if (stateSelector && !parent.id) {
						nextParent.id = id;
						nextParent.states = states;
					}

					if (this.props.isCardTree) {
						cssObject = injectStyles({ state, mq: platform, destination: cssObject, element });
					}

					// skip to next
					if (_.isEmpty(cssObject)) {
						return;
					}

					const payload = { cssObject, selector, element, mq: orderedMediaQueryArray };
					if (mediaValue) {
						styles += `${media} {\n`;
						mapCssObject({ ...payload, addTab: true });
						styles += '}\n';
					} else {
						mapCssObject(payload);
					}
				});
			});

			// go recursively down children
			mapChildren(element.children, nextParent);
		};

		const mapCssObject = ({ cssObject, selector, addTab, element, mq }: MapCssObject) => {
			const tab = addTab ? '\t' : '';

			const cssArray = _.entries(cssObject).sort(([a], [b]) => {
				// Why borderRadius? shorthand property should have less priority against particular property
				if (a === CUSTOM_CSS || b === CSS_PROPS.borders.borderRadius) return 1;
				if (b === CUSTOM_CSS) return -1;
				return a.localeCompare(b);
			});

			// unsupported css rules
			const cssArrayCustom = cssArray.filter(([prop]) => getCustomCssProp(element.type, prop));
			_.forEach(cssArrayCustom, css => {
				const [property, cssValue] = css;
				const { selectorPostfix, cssProperty, selectorPrefix } = getCustomCssProp(element.type, property)!;

				if (property === GLOBAL_BLOCK_DISPLAY) {
					// this property is assigned per card
					_.forOwn(cssValue, (value, key) => {
						const prefix = selectorPrefix?.({ cardId: key });

						styles += `${tab}${prefix} ${selector} {\n`;
						styles += `${tab}\t${_.kebabCase(cssProperty)}: ${value};\n`;
						styles += `${tab}}\n`;
					});
				} else {
					const postfix = selectorPostfix?.() || '';
					const prefix = selectorPrefix?.(cssValue as any) || '';

					styles += `${tab}${prefix} ${selector} ${postfix} {\n`;
					styles += `${tab}\t${_.kebabCase(cssProperty)}: ${cssValue};\n`;
					styles += `${tab}}\n`;
				}
			});

			// supported css rules
			styles += `${tab}${selector} {\n`;
			_.forEach(cssArray, css => {
				const [cssProperty, cssValue] = css;
				const hasValue = !_.isUndefined(cssValue) && cssValue !== '';
				if (hasValue && !getCustomCssProp(element.type, cssProperty)) {
					styles += `${tab}\t${CSSRule(cssProperty, cssValue, { mq })}\n`;
				}
			});
			styles += `${tab}}\n`;
		};

		mapChildren(elements);

		const t1 = performance.now();
		this.log(`rendering took ${(t1 - t0).toFixed(4)} milliseconds. %O `, elements);

		return styles;
	}

	render() {
		return createPortal(
			// @ts-expect-error ts-migrate FIXME
			<style storycards="storycards-css-json">{this.transformStylesToString()}</style>,
			document.head
		);
	}
}

type TCSSValue = string | number;
type TCSSProperty = string;
type TCSSParams = Pick<MapCssObject, 'mq'>;

const CSSValueMap = {
	backgroundImage: (v: TCSSValue, p: TCSSParams) => {
		if (typeof v !== 'string') return v;

		let result = v;

		if (v.match('imgix.net') && v.match('.gif') === null && v.match('.svg') === null && v !== 'none') {
			const start = v.substr(0, v.length - 1);
			const end = v.substr(v.length - 1);
			const hasParams = v.match(/\?/);

			const min = _.minBy(p.mq, ({ config: c }) => c.maxWidth || c.minWidth);
			const mobileSize =
				(min.config.maxWidth || min.config.minWidth) -
				(min.key === DEFAULT_MEDIA_QUERY_PLATFORMS.MOBILE ? 0 : 1);

			const params =
				window.innerWidth <= mobileSize
					? 'w=850&q=0.8'
					: 'fit=crop&max-w=1800&max-h=1200&q=80&auto=format,compress';
			result = `${start}${hasParams ? '&' : '?'}${params}${end}`;
		}

		return result;
	},
	gridAutoRows: (v: TCSSValue) => `minmax(auto, ${v})`,
	[CUSTOM_CSS]: (v: TCSSValue) => `-->; ${v}`,
	default: (v: TCSSValue) => v,
};

function CSSValue(property: TCSSProperty, value: TCSSValue, params: TCSSParams): TCSSValue {
	return CSSValueMap[property] ? CSSValueMap[property](value, params) : CSSValueMap.default(value);
}

function CSSRule(property: TCSSProperty, value: TCSSValue, params: TCSSParams) {
	return `${_.kebabCase(property)}: ${CSSValue(property, value, params)};`;
}

export default StylesRenderer;
